import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { postsService } from "@/services/posts.service";
import { tagsService, type TagShelf } from "@/services/tags.service";

export {
  buildShelfFilterState,
  buildShelfStashesHref,
  getShelfTagIds,
  resolveShelfLabel,
} from "@/lib/tagShelves";

const SHELVES_LIMIT = 6;
const SHELVES_MIN_POSTS = 3;
const SHELVES_POST_LIMIT = 10;

export function useShelves({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  const userId = user?.user_id ?? null;
  const utcDay = new Date().toISOString().slice(0, 10);

  return useQuery({
    queryKey: ["shelves", "user-shelves", userId, SHELVES_LIMIT, SHELVES_MIN_POSTS, SHELVES_POST_LIMIT, utcDay],
    queryFn: () =>
      tagsService.getShelves({ limit: SHELVES_LIMIT, minPosts: SHELVES_MIN_POSTS, postLimit: SHELVES_POST_LIMIT }),
    enabled: enabled && Boolean(userId),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/** The shelves' preview stashes in one read (the shelf feed carries only their ids), by id. */
export function useShelfPreviews(shelves: TagShelf[] | undefined) {
  const { user } = useAuth();
  const userId = user?.user_id ?? null;
  const ids = useMemo(
    () => [...new Set((shelves ?? []).flatMap(shelf => (shelf.posts ?? []).map(post => post.id)))].filter(Boolean).sort(),
    [shelves],
  );

  return useQuery({
    queryKey: ["shelf-posts", userId, ids.join("|")],
    queryFn: async () => (await postsService.batchGetPosts(ids)).posts ?? {},
    enabled: Boolean(userId) && ids.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
